import React from 'react';
import type { ObjectivePower } from './magnification';

interface MicroscopeProps {
  objective: ObjectivePower;
  lampOn: boolean;
  onLampToggle: () => void;
  onObjectiveChange: (objective: ObjectivePower) => void;
}

/** Accessible 2D microscope illustration; controls remain separate, real inputs. */
const Microscope: React.FC<MicroscopeProps> = ({ objective, lampOn, onLampToggle, onObjectiveChange }) => {
  const objectives: ObjectivePower[] = [4, 10, 40, 100];
  return (
    <div className="micro-scope-card">
      <svg className="micro-scope-art" viewBox="0 0 520 470" role="img" aria-labelledby="microscope-title microscope-description">
        <title id="microscope-title">Compound light microscope</title>
        <desc id="microscope-description">A labeled microscope with eyepiece, objective turret, stage, condenser, light source, arm, and coarse and fine focus knobs.</desc>
        <defs>
          <linearGradient id="scopeMetal" x1="0" x2="1"><stop stopColor="#7f91a1"/><stop offset=".5" stopColor="#e7eef3"/><stop offset="1" stopColor="#788996"/></linearGradient>
          <linearGradient id="scopeBase" x1="0" x2="0" y1="0" y2="1"><stop stopColor="#506272"/><stop offset="1" stopColor="#263441"/></linearGradient>
          <radialGradient id="scopeLamp"><stop stopColor="#fff7bd"/><stop offset=".65" stopColor="#ffce66"/><stop offset="1" stopColor="#f29d38"/></radialGradient>
        </defs>
        <ellipse cx="276" cy="438" rx="183" ry="18" fill="#101923" opacity=".35"/>
        <path d="M117 408 Q120 376 159 367 L336 367 Q392 370 414 407 L414 420 L117 420Z" fill="url(#scopeBase)" stroke="#17232e" strokeWidth="4"/>
        <path d="M170 368 Q223 329 310 351 L348 374 L322 390 Q275 371 215 392 L161 392Z" fill="url(#scopeMetal)" stroke="#536474" strokeWidth="4"/>
        <path d="M210 347 L248 323 L290 330 L275 360Z" fill="#253643"/>
        <path d="M271 345 C338 314 348 271 335 214 C326 176 290 145 265 126 L290 98 C337 129 374 163 387 214 C405 284 375 347 319 379Z" fill="url(#scopeMetal)" stroke="#506170" strokeWidth="5"/>
        <path d="M272 143 L304 119 L270 68 L237 89Z" fill="#344754" stroke="#17232e" strokeWidth="4"/>
        <path d="M229 95 L282 58 L254 19 Q244 7 232 16 L203 37 Q195 44 202 55Z" fill="url(#scopeMetal)" stroke="#425361" strokeWidth="5"/>
        <path d="M201 39 L256 12" stroke="#182630" strokeWidth="9" strokeLinecap="round"/>
        <path d="M308 224 L340 222 L348 278 L316 281Z" fill="#182630" stroke="#83929e" strokeWidth="4"/>
        <path d="M343 233 L370 230 L375 275 L349 278Z" fill="#6b7d89" stroke="#1d2b36" strokeWidth="4"/>
        <circle cx="321" cy="214" r="43" fill="#31434f" stroke="#172630" strokeWidth="8"/>
        <circle cx="321" cy="214" r="27" fill="#526573" stroke="#aebdc7" strokeWidth="3"/>
        <circle cx="321" cy="214" r="6" fill="#15232d"/>
        <path d="M140 259 L390 259 L378 279 L140 279Z" fill="#d3dee5" stroke="#71828f" strokeWidth="4"/>
        <rect x="237" y="257" width="53" height="22" rx="3" fill="#253846" stroke="#bac7cf" strokeWidth="3"/>
        <path d="M162 251 L185 251 L185 260 L162 260Z M337 251 L362 251 L362 260 L337 260Z" fill="#e7eef2" stroke="#596b79" strokeWidth="3"/>
        <path d="M250 280 L278 280 L285 331 L243 331Z" fill="#314754" stroke="#172630" strokeWidth="4"/>
        <ellipse cx="264" cy="337" rx="45" ry="12" fill="#465a68" stroke="#1d2d38" strokeWidth="4"/>
        <ellipse cx="264" cy="385" rx="28" ry="17" fill={lampOn ? 'url(#scopeLamp)' : '#303a42'} stroke="#182630" strokeWidth="5"/>
        <path d="M226 365 L300 365" stroke="#15232c" strokeWidth="7"/>
        <circle cx="355" cy="333" r="31" fill="#3d4f5c" stroke="#172630" strokeWidth="5"/>
        <circle cx="355" cy="333" r="20" fill="#687985" stroke="#aab7c0" strokeWidth="3"/>
        <circle cx="399" cy="351" r="17" fill="#7e8f9b" stroke="#263640" strokeWidth="5"/>
        <text x="28" y="70" className="scope-label">EYEPIECE · 10×</text>
        <path d="M125 67 L192 54" className="scope-callout"/>
        <text x="376" y="104" className="scope-label">OBJECTIVES</text>
        <path d="M373 111 L345 176" className="scope-callout"/>
        <text x="31" y="244" className="scope-label">STAGE + CLIPS</text>
        <path d="M136 246 L174 256" className="scope-callout"/>
        <text x="32" y="352" className="scope-label">CONDENSER</text>
        <path d="M127 348 L227 339" className="scope-callout"/>
        <text x="395" y="405" className="scope-label">ILLUMINATOR</text>
        <path d="M394 395 L306 384" className="scope-callout"/>
        <text x="412" y="304" className="scope-label">COARSE / FINE</text>
        <path d="M409 311 L381 328" className="scope-callout"/>
      </svg>
      <div className="scope-objective-dock" aria-label="Objective turret selection">
        <span>Objective turret</span>
        {objectives.map((power) => (
          <button key={power} type="button" className={objective === power ? 'selected' : ''} onClick={() => onObjectiveChange(power)} aria-pressed={objective === power} aria-label={`Rotate objective turret to ${power} times`}>
            {power}×
          </button>
        ))}
      </div>
      <button type="button" className={`scope-lamp-toggle ${lampOn ? 'is-on' : ''}`} onClick={onLampToggle} aria-pressed={lampOn}>
        {lampOn ? 'Illuminator on' : 'Illuminator off'}
      </button>
    </div>
  );
};

export default Microscope;
